import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, api, type LabelDetail } from '../api'
import { Card, Chip, CopyButton, Empty, shortDate } from '../components'
import { vocabulary } from '../vocabulary'

const l = vocabulary.labels

/** What the reprint turned out to be, once its content type is known. A PDF and images preview
 *  inline; anything else (a ZPL stream, an unknown binary) is offered for download instead. */
type Preview =
  | { kind: 'loading' }
  | { kind: 'pdf' }
  | { kind: 'image' }
  | { kind: 'other'; type: string }
  | { kind: 'error' }

/** Track whether the viewport is phone-sized, reactively. Mobile browsers can't render a PDF inline
 *  in an iframe, so the preview falls back to open/download there. */
function useNarrow(max = 700): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(`(max-width: ${max}px)`).matches)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${max}px)`)
    const onChange = () => setNarrow(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [max])
  return narrow
}

/**
 * Not a list — a lookup. A person pastes a label's public reference to pull up that one label, read
 * its details, and reprint it while it is still active. The reference is the whole query, and a
 * label this organization is not party to simply misses, so the screen never confirms a reference
 * the person has no business seeing.
 *
 * The reprint can be any format the sender rendered — a PDF, a PNG, a ZPL stream. The screen first
 * reads the content type (a cheap same-origin fetch), then loads the label itself from the /print
 * proxy: a PDF frames, an image shows, and anything else offers a download. Using the proxy's http
 * URL (not a blob) is what lets Chrome fit the PDF to width.
 */
export default function Labels({ accountId }: { accountId: string }) {
  const [query, setQuery] = useState('')
  const [state, setState] = useState<'idle' | 'searching' | 'found' | 'missing' | 'error'>('idle')
  const [label, setLabel] = useState<LabelDetail | null>(null)
  const [message, setMessage] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const narrow = useNarrow()

  async function search(event: FormEvent) {
    event.preventDefault()
    const ref = query.trim()
    if (!ref) return
    setState('searching')
    setLabel(null)
    setMessage('')
    setPreview(null)
    try {
      const found = await api.labelByRef(accountId, ref)
      setLabel(found)
      setState('found')
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 404) {
        setState('missing')
      } else {
        setState('error')
        setMessage(failure instanceof ApiError ? failure.message : l.lookupError)
      }
    }
  }

  // Read only the content type — the label itself is loaded from /print by the preview.
  useEffect(() => {
    if (!label || label.status !== 'active') {
      setPreview(null)
      return
    }
    let live = true
    setPreview({ kind: 'loading' })
    fetch(`/api/reprint/${encodeURIComponent(label.label_id)}`)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status))
        const type = (response.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase()
        if (!live) return
        if (type === 'application/pdf') setPreview({ kind: 'pdf' })
        else if (type.startsWith('image/')) setPreview({ kind: 'image' })
        else setPreview({ kind: 'other', type: type || 'file' })
      })
      .catch(() => { if (live) setPreview({ kind: 'error' }) })
    return () => { live = false }
  }, [label])

  const statusChip = label && (
    label.status === 'active' ? <Chip kind="ok">{l.statusActive}</Chip>
      : label.status === 'expired' ? <Chip kind="wait">{l.statusExpired}</Chip>
        : <Chip kind="stop">{l.statusErased}</Chip>
  )

  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <h1>{l.title}</h1>
        <p className="small muted">{l.subhead}</p>
      </div>

      <form className="row" style={{ gap: 8 }} onSubmit={search}>
        <input
          className="input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={l.placeholder}
          aria-label={l.refAria}
          autoFocus
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button className="btn primary" type="submit" disabled={state === 'searching' || !query.trim()}>
          {state === 'searching' ? l.finding : l.findLabel}
        </button>
      </form>

      {state === 'idle' && (
        <Empty line={l.idlePrompt} />
      )}
      {state === 'missing' && (
        <p className="banner wait">{l.missing}</p>
      )}
      {state === 'error' && <p className="banner stop">{message}</p>}

      {state === 'found' && label && (
        <div className="split-2">
          <Card title={l.cardTitle} action={statusChip}>
            <div className="stack" style={{ gap: 12 }}>
              <div className="stack" style={{ gap: 2 }}>
                <span className="label">{l.publicReference}</span>
                <span className="mono" style={{ wordBreak: 'break-all' }}>{label.public_ref}</span>
              </div>
              <div className="between"><span className="small faint">{l.published}</span><span className="small nums">{shortDate(label.published_at)}</span></div>
              <div className="between"><span className="small faint">{l.expires}</span><span className="small nums">{shortDate(label.expires_at)}</span></div>
              <div className="between"><span className="small faint">{l.publishedBy}</span><span className="small">{label.published_by ?? '—'}</span></div>
              {label.reprint_url && (
                <div className="row wrap-row" style={{ borderTop: '1px solid var(--line)', paddingTop: 12, gap: 8 }}>
                  <CopyButton value={label.reprint_url} label={l.copyPrintLink} />
                  <a className="btn small" href={label.reprint_url} target="_blank" rel="noreferrer">{l.open}</a>
                </div>
              )}
            </div>
          </Card>

          <Card title={l.printPreview}>
            <LabelPreview label={label} preview={preview} narrow={narrow} />
          </Card>
        </div>
      )}
    </>
  )
}

function LabelPreview({ label, preview, narrow }: { label: LabelDetail; preview: Preview | null; narrow: boolean }) {
  const printSrc = `/print/${encodeURIComponent(label.label_id)}`

  if (label.status === 'erased') {
    return <Empty line={l.erasedPreview} />
  }
  if (label.status === 'expired') {
    return <Empty line={l.expiredPreview} />
  }
  if (!preview || preview.kind === 'loading') {
    return <div className="skeleton preview-frame" aria-busy="true" aria-label={l.loadingPreviewAria} />
  }
  if (preview.kind === 'error') {
    return (
      <Empty
        line={l.reprintError}
        action={label.reprint_url
          ? <a className="btn small" href={label.reprint_url} target="_blank" rel="noreferrer">{l.openNewTab}</a>
          : undefined}
      />
    )
  }
  if (preview.kind === 'pdf') {
    // Mobile browsers have no inline PDF viewer — an iframe just shows "content blocked" — so on a
    // phone the PDF opens or downloads instead.
    if (narrow) {
      return (
        <Empty
          line={l.pdfMobile}
          action={
            <div className="row wrap-row" style={{ justifyContent: 'center', gap: 8 }}>
              <a className="btn small primary" href={printSrc} download={`${label.public_ref}.pdf`}>{l.downloadPdf}</a>
              {label.reprint_url && <a className="btn small" href={label.reprint_url} target="_blank" rel="noreferrer">{l.open}</a>}
            </div>
          }
        />
      )
    }
    // #view=FitH fits the page to the frame width, so Chrome's PDF viewer doesn't scroll sideways.
    // It works only because /print is an http URL — Chrome ignores the param on a blob:.
    return <iframe className="preview-frame" src={`${printSrc}#view=FitH`} title={`${l.renderedLabelAria} ${label.public_ref}`} />
  }
  if (preview.kind === 'image') {
    return (
      <div className="preview-frame" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, overflow: 'auto' }}>
        <img src={printSrc} alt={`${l.renderedLabelAria} ${label.public_ref}`} style={{ maxWidth: '100%', maxHeight: '100%' }} />
      </div>
    )
  }
  return (
    <Empty
      line={`${l.otherFilePrefix}${preview.type}${l.otherFileSuffix}`}
      action={
        <div className="row wrap-row" style={{ justifyContent: 'center', gap: 8 }}>
          <a className="btn small primary" href={printSrc} download={label.public_ref}>{l.download}</a>
          {label.reprint_url && <a className="btn small" href={label.reprint_url} target="_blank" rel="noreferrer">{l.open}</a>}
        </div>
      }
    />
  )
}
